import { create } from 'zustand';

import { preference } from '../platform';

/**
 * What the person chose, which is not the same as what is displayed.
 *
 * `system` is the default and the only honest one to start from: the device
 * already knows whether it is night, and an app that ignores that answer and
 * asks again is one more setting nobody wanted.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

/** What is actually painted, once the system's own preference is resolved. */
export type ThemeName = 'light' | 'dark';

const STORAGE_KEY = 'liniya.theme';

function readChoice(): ThemeChoice {
  const stored = preference().get(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

interface ThemeState {
  choice: ThemeChoice;
  /** What the platform reports right now; `null` before anything asked. */
  systemTheme: ThemeName | null;
  resolved: ThemeName;

  setChoice: (choice: ThemeChoice) => void;
  /** Called by each client when the device's own setting changes. */
  setSystemTheme: (theme: ThemeName | null) => void;
  /**
   * Re-read the stored choice.
   *
   * The store is built when the module loads, which on a phone is before the
   * durable preferences have been read off disk — so the first answer is
   * always the default. Asking again once they are loaded is what makes the
   * choice survive a restart.
   */
  hydrate: () => void;
}

function resolve(choice: ThemeChoice, system: ThemeName | null): ThemeName {
  if (choice !== 'system') return choice;
  return system ?? 'light';
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  choice: readChoice(),
  systemTheme: null,
  resolved: resolve(readChoice(), null),

  setChoice: (choice) => {
    preference().set(STORAGE_KEY, choice);
    set({ choice, resolved: resolve(choice, get().systemTheme) });
  },

  setSystemTheme: (systemTheme) =>
    set({ systemTheme, resolved: resolve(get().choice, systemTheme) }),

  hydrate: () => {
    const choice = readChoice();
    set({ choice, resolved: resolve(choice, get().systemTheme) });
  },
}));

/** Readable outside React — the mobile palette is not a hook. */
export function activeTheme(): ThemeName {
  return useThemeStore.getState().resolved;
}

export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: 'Как в системе',
  light: 'Светлая',
  dark: 'Тёмная',
};
